import { browser } from '$app/environment';

import { Modal, type ModalOption } from '$shared/models/Modal';
import { modalStore } from '$shared/stores/modalStore';
import { ModalFakeInputElementId } from '$shared/constants/global';
import { ConfirmPopup } from '$shared/components';
import ModalEvent from '$shared/constants/ModalEvent';
import type { PopupEventMap } from '$shared/lib/serviceInterfaces/IPopupService';
import type { Component } from 'svelte';
import type { IModal } from '$shared/interfaces/IModal';

export class ModalService {
  public open(type: number | undefined, options: ModalOption): Modal {
    const modal = Modal.from(type, options);
    modalStore.create(modal);
    browser && document.getElementById(ModalFakeInputElementId)?.focus();
    return modal;
  }

  public add(type: number, options: ModalOption): Modal {
    const modal = Modal.from(type, options);
    modalStore.create(modal);
    return modal;
  }

  public updateProps(modal: Modal, props: ObjectType<any>): void {
    if (!props) throw new Error('Invalid modal props param value');
    this.setModalProps(modal, props);
  }

  public setLoading(modal: Modal, loading: boolean): void {
    this.setModalProps(modal, { loading });
  }

  public close(modal: Modal): void {
    const modalList = modalStore.list;
    const list = modalList.filter((item: Modal) => item.id !== modal.id);
    modalStore.setList(list);
  }

  public refresh(): void {
    const modalList = modalStore.list;
    const newlist = modalList
      .filter((item: Modal) => item.show)
      .map((item: Modal) => {
        item.props = { ...item.props };
        return item;
      });
    modalStore.setList(newlist);
  }

  /** 2DO: This will be removed in future, right there are multiple usages */
  public openConfirmationModal(
    props: ObjectType<any>,
    cb?: () => Promise<void>,
    events?: PopupEventMap,
    component: Component = ConfirmPopup
  ): void {
    this.open(undefined, {
      component,
      props,
      events: {
        [ModalEvent.ok]: async (modal: IModal, data?: unknown) => {
          try {
            this.setLoading(modal, true);
            await cb?.();
            await events?.[ModalEvent.ok]?.(modal, data);
          } finally {
            this.setLoading(modal, false);
            modal.close();
          }
        },
        [ModalEvent.closeCallback]: () => events?.[ModalEvent.closeCallback]?.()
      }
    });
  }

  /**
   * Opens a confirmation modal and returns a Promise<boolean> that always settles.
   *
   * Uses an `isConfirmed` flag to make `closeCallback` the single settlement point:
   * - OK clicked  → `isConfirmed = true` → cb runs → modal.close() → closeCallback → resolve(true)
   * - Cancel/Escape/backdrop → `isConfirmed = false` → modal.close() → closeCallback → resolve(false)
   *
   * This avoids the double-resolve race that occurs when closeCallback fires unconditionally
   * inside the `finally` block of the OK handler.
   *
   * @param props - Modal props (title, desc, okLabel, etc.)
   * @param cb - Optional async callback to run after confirmation (receives data from the ok event)
   * @param component - Popup component to render (defaults to ConfirmPopup)
   */
  protected openConfirmationModalAsync<TData = undefined>(
    props: ObjectType<any>,
    cb?: (data?: TData) => Promise<void>,
    component: Component = ConfirmPopup
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      let isConfirmed = false;

      this.open(undefined, {
        component,
        props,
        events: {
          [ModalEvent.ok]: async (modal: IModal, data?: TData) => {
            isConfirmed = true;
            try {
              this.setLoading(modal as Modal, true);
              await cb?.(data);
            } catch (err) {
              reject(err);
            } finally {
              this.setLoading(modal as Modal, false);
              modal.close();
            }
          },
          [ModalEvent.closeCallback]: () => resolve(isConfirmed)
        }
      });
    });
  }

  private setModalProps(modal: Modal, props: ObjectType<any>): void {
    if (!modal?.id) throw new Error('Invalid Modal Id');

    const modalPopup = modalStore.list?.find((item) => item.id === modal.id);
    if (!modalPopup) throw new Error('Modal not found');
    modalPopup.updateProps(props);
    modalStore.updateList(modalPopup);
  }
}

export const modalService = new ModalService();
